import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, Target, Zap, Rocket, BarChart3, DollarSign, Users } from 'lucide-react';
import { firebaseApi, ContentGenerationRequest } from '../services/firebaseApi';

interface PhoenixMetrics {
  legalRisk: number;
  financialStress: number;
  emotionalStrain: number;
  opportunityWindow: number;
}

interface DeploymentPhase {
  id: number;
  name: string;
  description: string;
  status: 'pending' | 'in-progress' | 'completed';
  progress: number;
}

const PhoenixRisingSystem: React.FC = () => {
  const [metrics, setMetrics] = useState<PhoenixMetrics>({
    legalRisk: 85,
    financialStress: 90,
    emotionalStrain: 75,
    opportunityWindow: 15
  });

  const [phases, setPhases] = useState<DeploymentPhase[]>([
    {
      id: 1,
      name: 'AI Systems Integration',
      description: 'Deploy Claude, OpenAI, and Gemini APIs',
      status: 'pending',
      progress: 0
    },
    {
      id: 2,
      name: 'Content Engine',
      description: 'Multi-platform content generation system',
      status: 'pending',
      progress: 0
    },
    {
      id: 3,
      name: 'Revenue Systems',
      description: 'Payment processing and affiliate tracking',
      status: 'pending',
      progress: 0
    },
    {
      id: 4,
      name: 'Analytics Dashboard',
      description: 'Real-time business intelligence',
      status: 'pending',
      progress: 0
    },
    {
      id: 5,
      name: 'Business Automation',
      description: 'Complete workflow automation',
      status: 'pending',
      progress: 0
    },
    {
      id: 6,
      name: 'Go Live',
      description: 'Launch to creator360automation.com',
      status: 'pending',
      progress: 0
    }
  ]);

  const [isTransforming, setIsTransforming] = useState(false);
  const [transformationProgress, setTransformationProgress] = useState(0);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentProgress, setDeploymentProgress] = useState(0);
  const [backendStatus, setBackendStatus] = useState<'checking' | 'connected' | 'error'>('checking');

  const startTransformation = async () => {
    setIsTransforming(true);
    setTransformationProgress(0);
    
    try {
      // Track analytics
      await firebaseApi.trackAnalytics({
        userId: 'phoenix-user',
        event: 'transformation_started',
        data: { metrics: metrics }
      });

      const interval = setInterval(() => {
        setTransformationProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsTransforming(false);
            // Update metrics after transformation
            setMetrics({
              legalRisk: 25,
              financialStress: 20,
              emotionalStrain: 15,
              opportunityWindow: 85
            });
            
            // Track completion
            void firebaseApi.trackAnalytics({
              userId: 'phoenix-user',
              event: 'transformation_completed',
              data: { newMetrics: { legalRisk: 25, financialStress: 20, emotionalStrain: 15, opportunityWindow: 85 } }
            });
            
            return 100;
          }
          return prev + 2;
        });
      }, 100);
    } catch (error) {
      console.error('Error during transformation:', error);
      setIsTransforming(false);
    }
  };

  const startDeployment = async () => {
    setIsDeploying(true);
    setDeploymentProgress(0);
    
    try {
      // Track deployment start
      await firebaseApi.trackAnalytics({
        userId: 'phoenix-user',
        event: 'deployment_started',
        data: { phases: phases }
      });

      const interval = setInterval(() => {
        setDeploymentProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsDeploying(false);
            // Mark all phases as completed
            setPhases(prevPhases => 
              prevPhases.map(phase => ({
                ...phase,
                status: 'completed' as const,
                progress: 100
              }))
            );
            
            // Track deployment completion
            void firebaseApi.trackAnalytics({
              userId: 'phoenix-user',
              event: 'deployment_completed',
              data: { status: 'success' }
            });
            
            return 100;
          }
          return prev + 1;
        });
      }, 200);
    } catch (error) {
      console.error('Error during deployment:', error);
      setIsDeploying(false);
    }
  };

  const getMetricColor = (value: number) => {
    if (value >= 70) return 'text-red-500';
    if (value >= 40) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getMetricIcon = (value: number) => {
    if (value >= 70) return <TrendingDown className="w-5 h-5" />;
    return <TrendingUp className="w-5 h-5" />;
  };

  // Check backend status on component mount
  useEffect(() => {
    const checkBackendStatus = async () => {
      try {
        await firebaseApi.healthCheck();
        setBackendStatus('connected');
      } catch (error) {
        console.error('Backend connection failed:', error);
        setBackendStatus('error');
      }
    };

    void checkBackendStatus();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 text-white">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="phoenix-gradient absolute inset-0 opacity-20"></div>
        <div className="relative z-10 container mx-auto px-4 py-8">
          <div className="text-center mb-12">
            <h1 className="text-6xl font-bold mb-4 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
              🔥 Phoenix Rising
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Transform crisis into opportunity with AI-powered business automation
            </p>
          </div>
        </div>
      </div>

      {/* Crisis to Opportunity Transformation */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Current Crisis Metrics */}
          <div className="crisis-to-opportunity rounded-2xl p-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Target className="w-8 h-8 mr-3" />
              Current Crisis Metrics
            </h2>
            <div className="space-y-4">
              <div className="metric-card rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg">Legal Risk</span>
                  <div className="flex items-center">
                    {getMetricIcon(metrics.legalRisk)}
                    <span className={`ml-2 font-bold ${getMetricColor(metrics.legalRisk)}`}>
                      {metrics.legalRisk}%
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                  <div 
                    className="bg-red-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${metrics.legalRisk}%` }}
                  ></div>
                </div>
              </div>

              <div className="metric-card rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg">Financial Stress</span>
                  <div className="flex items-center">
                    {getMetricIcon(metrics.financialStress)}
                    <span className={`ml-2 font-bold ${getMetricColor(metrics.financialStress)}`}>
                      {metrics.financialStress}%
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                  <div 
                    className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${metrics.financialStress}%` }}
                  ></div>
                </div>
              </div>

              <div className="metric-card rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg">Emotional Strain</span>
                  <div className="flex items-center">
                    {getMetricIcon(metrics.emotionalStrain)}
                    <span className={`ml-2 font-bold ${getMetricColor(metrics.emotionalStrain)}`}>
                      {metrics.emotionalStrain}%
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                  <div 
                    className="bg-yellow-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${metrics.emotionalStrain}%` }}
                  ></div>
                </div>
              </div>

              <div className="metric-card rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg">Opportunity Window</span>
                  <div className="flex items-center">
                    <TrendingUp className="w-5 h-5" />
                    <span className="ml-2 font-bold text-green-500">
                      {metrics.opportunityWindow}%
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${metrics.opportunityWindow}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <button
              onClick={startTransformation}
              disabled={isTransforming}
              className="mt-6 w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold py-3 px-6 rounded-lg hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 disabled:opacity-50"
            >
              {isTransforming ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                  Transforming... {transformationProgress}%
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <Zap className="w-5 h-5 mr-2" />
                  Start Transformation
                </div>
              )}
            </button>
          </div>

          {/* Deployment Roadmap */}
          <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Rocket className="w-8 h-8 mr-3" />
              Deployment Roadmap
            </h2>
            <div className="space-y-4">
              {phases.map((phase) => (
                <div key={phase.id} className="deployment-phase bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">{phase.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      phase.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      phase.status === 'in-progress' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {phase.status}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mb-3">{phase.description}</p>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${phase.progress}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={startDeployment}
              disabled={isDeploying}
              className="mt-6 w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-3 px-6 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50"
            >
              {isDeploying ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Deploying... {deploymentProgress}%
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <Rocket className="w-5 h-5 mr-2" />
                  Start Deployment
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Backend Status */}
        <div className="mb-6">
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold">Backend Status</span>
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-2 ${
                  backendStatus === 'connected' ? 'bg-green-500' :
                  backendStatus === 'error' ? 'bg-red-500' : 'bg-yellow-500'
                }`}></div>
                <span className={`text-sm ${
                  backendStatus === 'connected' ? 'text-green-400' :
                  backendStatus === 'error' ? 'text-red-400' : 'text-yellow-400'
                }`}>
                  {backendStatus === 'connected' ? 'Connected' :
                   backendStatus === 'error' ? 'Error' : 'Checking...'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="metric-card rounded-lg p-6 text-center">
            <BarChart3 className="w-8 h-8 mx-auto mb-2 text-blue-400" />
            <div className="text-2xl font-bold">AI Systems</div>
            <div className="text-gray-400">Claude, OpenAI, Gemini</div>
          </div>
          <div className="metric-card rounded-lg p-6 text-center">
            <DollarSign className="w-8 h-8 mx-auto mb-2 text-green-400" />
            <div className="text-2xl font-bold">Revenue</div>
            <div className="text-gray-400">Payment Processing</div>
          </div>
          <div className="metric-card rounded-lg p-6 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 text-purple-400" />
            <div className="text-2xl font-bold">Analytics</div>
            <div className="text-gray-400">Real-time Dashboard</div>
          </div>
          <div className="metric-card rounded-lg p-6 text-center">
            <Zap className="w-8 h-8 mx-auto mb-2 text-yellow-400" />
            <div className="text-2xl font-bold">Automation</div>
            <div className="text-gray-400">Business Workflows</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoenixRisingSystem; 